<template>
  <div class="w-full relative">
    <div
      class="rounded-8 flex flex-row border-t border-l border-r border-checkbox border-b border-gray-500 lg:p-6 px-4 py-3 cursor-pointer transition-all duration-200 ease-in-out"
      :class="[isOpen ? 'bg-creme-50' : 'bg-creme-200']"
      @click="toggle"
    >
      <div class="flex flex-row w-full items-center justify-between">
        <div
          v-if="selections.length === 0"
          class="text-body-2 xl:text-body-1 text-gray-500"
        >
          {{ placeholder }}
        </div>
        <div v-else class="text-body-2 xl:text-body-1 text-gray-900">
          {{ getSelections }}
        </div>
        <img
          class="transition-transform fill-blue-500"
          src="@/assets/ico_arrow_down.svg"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </div>

    <div
      class="open transition-all duration-200 ease-in-out absolute"
      :class="[
        isOpen ? 'rounded-b-8 border border-gray-500 w-full h-auto' : 'h-0',
      ]"
    >
      <div class="flex mt-4">
        <div class="my-4 pl-6 w-1/2 flex flex-row items-center">
          <Checkbox :ref="all" @input="selectPerimeter(all)" />
          <div
            class="text-gray-900 text-body-2 xl:text-body-1 ml-2 flex items-center"
          >
            All Atlanta Locations
          </div>
        </div>

        <div class="my-4 pl-6 w-1/2 flex flex-row items-center">
          <Checkbox :ref="outside" @input="outsideAtlanta()" />
          <div
            class="text-gray-900 text-body-2 xl:text-body-1 ml-2 flex items-center"
          >
            Outside of Atlanta
          </div>
        </div>
      </div>

      <div class="open-wrapper">
        <div class="ml-6 my-4 w-full" v-for="(c, i) in choices" :key="i">
          <div class="text-gray-900 font-extrabold text-body-2 mb-6">
            {{ c.name }}
          </div>
          <div v-for="(v, i) in c.values" :key="i" class="flex flex-row py-2">
            <Checkbox
              ref="chk-item"
              :class="c.name.toLowerCase().includes(outside) ? outside : inside"
              :id="JSON.stringify(v)"
              @input="checkboxInput(v, true)"
            />
            <div
              class="text-gray-900 text-body-2 xl:text-body-1 ml-4 flex items-center"
            >
              {{ v.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from "@/components/inputs/Checkbox.vue";

export default {
  name: "LocationsDropdown",
  components: {
    Checkbox,
  },
  props: {
    choices: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Choose",
    },
  },
  data() {
    return {
      inside: "inside",
      outside: "outside",
      all: "all",
      isOpen: false,
      selections: [],
      visibleSelections: [],
      listener: null,
    };
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    checkboxInput(v, clicked) {
      // If selection already exists, just untick it
      if (this.selections.indexOf(v.value) > -1) {
        this.selections.splice(this.selections.indexOf(v.value), 1);
        this.visibleSelections.splice(
          this.visibleSelections.indexOf(v.name),
          1
        );
      } else {
        if (clicked) {
          if (this.$refs["outside"].$data.checked) {
            this.visibleSelections = [];
            this.selections = [];
            this.$refs["outside"].$data.checked = false;
          }
        }
        this.selections.push(v.value);
        this.visibleSelections.push(v.name);
      }

      this.$emit("input", this.selections.join(";"));
    },
    outsideAtlanta() {
      this.visibleSelections = [];
      this.selections = [];
      if (this.$refs["outside"].$data.checked) {
        this.selections.push("Outside of Atlanta");
        this.visibleSelections.push("Outside of Atlanta");
      }
      this.$refs["all"].$data.checked = false;
      this.$refs["chk-item"].forEach((checkbox) => {
        checkbox.$data.checked = false;
      });
    },
    selectPerimeter(perimiter) {
      const shouldCheck = this.$refs[perimiter].checked;

      this.resetCheckboxes(perimiter);

      this.$refs["chk-item"].forEach((checkbox) => {
        // If perimiter is "inside" or "outside"
        if (
          checkbox.$data.checked !== shouldCheck &&
          [...checkbox.$el.classList].includes(perimiter)
        ) {
          checkbox.$data.checked = shouldCheck;
          this.checkboxInput(JSON.parse(checkbox.$el.id));
        }

        // If all checkboxes have to be selected
        if (checkbox.$data.checked !== shouldCheck && perimiter === this.all) {
          checkbox.$data.checked = shouldCheck;
          this.checkboxInput(JSON.parse(checkbox.$el.id));
        }
      });

      this.$refs["outside"].$data.checked = false;
    },
    resetCheckboxes(perimiter) {
      const allLocationsRef = this.$refs[this.all];
      const outsideLocationsRef = this.$refs[this.outside];

      // If one perimiter choice is already selected, deselect it
      if (perimiter === this.all && outsideLocationsRef.$data.checked) {
        outsideLocationsRef.$data.checked = false;
      }
      if (perimiter === this.outside && allLocationsRef.$data.checked) {
        allLocationsRef.$data.checked = false;
      }

      this.$refs["chk-item"].forEach(
        (checkbox) => (checkbox.$data.checked = false)
      );
      this.visibleSelections = [];
      this.selections = [];
    },
    getTotalCount() {
      let count = 0;
      this.choices.forEach((c) => {
        count += c.values.length;
      });
      return count;
    },
  },
  computed: {
    getSelections() {
      if (this.visibleSelections.length <= 3) {
        return this.visibleSelections.join(", ");
      } else {
        return `${this.visibleSelections.length} locations selected`;
      }
    },
  },
  created() {
    this.listener = window.addEventListener("click", (e) => {
      if (!this.$el.contains(e.target)) {
        if (this.isOpen) {
          this.toggle();
        }
      }
    });
  },
  beforeDestroy() {
    if (this.listener !== null) {
      window.removeEventListener(this.listener);
    }
  },
};
</script>
<style scoped>
.open {
  z-index: 100;
  @apply flex flex-col -mt-1 overflow-y-scroll overflow-x-hidden bg-creme-200;
}
.open-wrapper {
  @apply flex flex-col xl:flex-row;
}
</style>
